import React, { useMemo } from 'react'
import {
  Box, Collapse, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, makeStyles, useMediaQuery, useTheme
} from '@material-ui/core'

const useStyles = makeStyles((theme) => ({
  wrap: {
    overflowX: 'auto',
    border: '1px solid #e3e6f2',
    borderRadius: 10,
  },
  headCell: {
    background: '#1a237e',
    color: '#fff',
    fontWeight: 700,
    fontSize: 11.5,
    whiteSpace: 'nowrap',
    padding: '8px 10px',
  },
  cell: { fontSize: 12, padding: '7px 10px', whiteSpace: 'nowrap' },

  // ───── نمای موبایل: هر ردیف = یک کارت ─────
  cardList: {
    display: 'flex', flexDirection: 'column', gap: 10,
  },
  rowCard: {
    border: '1px solid #e3e6f2',
    borderRadius: 12,
    background: '#fff',
    overflow: 'hidden',
  },
  rowHead: {
    background: 'linear-gradient(135deg, #eef1ff 0%, #ffffff 100%)',
    borderBottom: '1px solid #e8eaf6',
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    flexWrap: 'wrap',
  },
  rowTitle: { fontWeight: 800, fontSize: 13.5, color: '#1a237e' },
  rowIndex: {
    fontWeight: 800, fontSize: 11.5, color: '#fff',
    background: '#1a237e', borderRadius: 999, padding: '1px 9px',
  },
  rowBody: { padding: '6px 12px 10px' },
  fieldRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '4px 0',
    borderBottom: '1px dashed #eef0f8',
    '&:last-child': { borderBottom: 'none' },
  },
  fieldLabel: {
    color: '#5a6b8c', fontSize: 12, fontWeight: 700,
    minWidth: 118, flexShrink: 0, paddingTop: 1,
  },
  fieldValue: { color: '#1a237e', fontSize: 12.5, fontWeight: 600, flex: 1, wordBreak: 'break-word' },
}))

/**
 * جدولِ «واکنش‌گرا»: در دسکتاپ یک جدولِ معمولی، در موبایل هر ردیف به یک کارت
 * تبدیل می‌شود (برچسب: مقدار) ⇒ بدون اسکرولِ افقی و بدون ستونِ پنهان.
 *
 * columns: [{ key, label, align, width, nowrap, render(row), primary, hideOnMobile }]
 */
export default function ResponsiveTable({
  columns = [],
  rows = [],
  rowKey = (r, i) => (r && (r.id ?? r.key)) ?? i,
  maxHeight = 450,
  stickyHeader = true,
  emptyText = 'موردی یافت نشد.',
  showIndex = false,
  indexLabel = 'ردیف',
  cardHeaderExtra = null,
  expandedKey = null,
  onToggleExpand = null,
  renderExpanded = null,
}) {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const list = useMemo(() => (Array.isArray(rows) ? rows : []), [rows])

  if (!list.length) {
    return (
      <Box style={{ padding: 18, textAlign: 'center', color: '#7a80a3' }}>{emptyText}</Box>
    )
  }

  // ───────────── موبایل: کارت ─────────────
  if (isMobile) {
    return (
      <div className={classes.cardList}>
        {list.map((row, i) => {
          const key = rowKey(row, i)
          const primaryCols = columns.filter((c) => c.primary && !c.hideOnMobile)
          const bodyCols = columns.filter((c) => !c.primary && !c.hideOnMobile)
          const isOpen = expandedKey !== null && expandedKey === key
          return (
            <React.Fragment key={key}>
              <div className={classes.rowCard}>
                <div className={classes.rowHead}>
                  <div className={classes.rowTitle}>
                    {primaryCols.length
                      ? primaryCols.map((c) => (
                        <span key={c.key} style={{ marginInlineEnd: 8 }}>
                          {c.render ? c.render(row) : row[c.key]}
                        </span>
                      ))
                      : ''}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {showIndex && <span className={classes.rowIndex}>{indexLabel} {i + 1}</span>}
                    {cardHeaderExtra}
                  </div>
                </div>
                <div className={classes.rowBody}>
                  {bodyCols.map((c) => (
                    <div className={classes.fieldRow} key={c.key}>
                      <div className={classes.fieldLabel}>{c.label}</div>
                      <div className={classes.fieldValue}>
                        {c.render ? c.render(row) : (row[c.key] ?? '—')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {renderExpanded && (
                <Collapse in={isOpen} timeout='auto' unmountOnExit>
                  <Box style={{ background: '#ecebeb', borderRadius: 10, padding: 6 }}>
                    {renderExpanded(row)}
                  </Box>
                </Collapse>
              )}
            </React.Fragment>
          )
        })}
      </div>
    )
  }

  // ───────────── دسکتاپ: جدول ─────────────
  return (
    <TableContainer className={classes.wrap} component={Paper} style={{ maxHeight }}>
      <Table size="small" stickyHeader={stickyHeader}>
        <TableHead>
          <TableRow>
            {showIndex && <TableCell className={classes.headCell} align="center">{indexLabel}</TableCell>}
            {columns.map((c) => (
              <TableCell
                key={c.key}
                className={classes.headCell}
                align={c.align || 'center'}
                style={c.width ? { width: c.width, minWidth: c.width } : undefined}
              >
                {c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {list.map((row, i) => {
            const key = rowKey(row, i)
            const isOpen = expandedKey !== null && expandedKey === key
            return (
              <React.Fragment key={key}>
                <TableRow>
                  {showIndex && (
                    <TableCell className={classes.cell} align="center">{i + 1}</TableCell>
                  )}
                  {columns.map((c) => (
                    <TableCell
                      key={c.key}
                      className={classes.cell}
                      align={c.align || 'center'}
                      style={c.nowrap === false ? { whiteSpace: 'normal' } : undefined}
                    >
                      {c.render ? c.render(row) : (row[c.key] ?? '—')}
                    </TableCell>
                  ))}
                </TableRow>
                {renderExpanded && (
                  <TableRow style={{ background: '#ecebeb' }}>
                    <TableCell style={{ padding: 0 }} colSpan={columns.length + (showIndex ? 1 : 0)}>
                      <Collapse in={isOpen} timeout='auto' unmountOnExit>
                        <Box margin={1}>{renderExpanded(row)}</Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
